import Amplify, { API, graphqlOperation } from 'aws-amplify';
import * as queries from "@/graphql/queries";

export default {
  mounted () {
    this.$nextTick(function () {
      // Update the header when the view mounts
      this.updateHeader();

      // Resize in case the header has changed size
      this.resizeHandler();

      // We only need to update Breadcrumbs for "New" pages during mounted() 
      // View mode is covered by watch on recordData below.
      if (this.isNewCaseView() || this.isNewServiceView() || this.isNewProductView()) {
        this.updateBreadcrumbs();
      }
    });
  },
  beforeRouteLeave (to, from, next) {
    // Reset the header before changing route
    this.resetToolbar();

    // Reset the header before changing route
    this.resetHeader();
    
    // Resize in case the header has changed size
    this.resizeHandler();

    //Always reset breadcrumbs
    this.$Breadcrumbs.items = [];

    next();
  },
  created() {
    window.addEventListener("resize", this.resizeHandler);
  },
  destroyed() {
    window.removeEventListener("resize", this.resizeHandler);
  },
  methods: {
    async updateBreadcrumbs() {
      let crumbs = [];

      /*
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      ACCOUNT
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      */
      if (this.isAccountView() && !this.isNewAccountView() && this.recordData && this.recordData.id) {
        // Add link to home
        crumbs.push({
          label: "Home",
          href: '/'
        });

        crumbs.push({
          label: "Accounts",
          href: '/accounts'
        });

        // Add link for current page
        crumbs.push({
          label: this.recordData.name,
          href: '/accounts/' + this.$route.params.id
        });
      }

      /*
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      SERVICE
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      */
      if (this.isServiceView()) {
        // Add link to home
        crumbs.push({
          label: "Home",
          href: '/'
        });

        // View/Edit Service
        if (!this.isNewServiceView()) {
          if (this.recordData && this.recordData.id) {
            // Add link to account
            let account = this.recordData.account;
            if (account && account.id) {
              crumbs.push({
                label: account.name,
                href: '/accounts/' + account.id
              });
            }

            // Add link for current page
            crumbs.push({
              label: this.recordData.name,
              href: '/services/' + this.$route.params.id
            });
          }
        // New Service
        } else {
          let serviceAccountId = this.$route.query.serviceAccountId;
          if (serviceAccountId) {
            let operation = graphqlOperation(queries.getAccount, {id: serviceAccountId});
            const result = await API.graphql(operation);
            if (result && result.data && result.data.getAccount) {
              let account = result.data.getAccount;
              crumbs.push({
                label: account.name,
                href: '/accounts/' + account.id
              });
            }
          }
  
          // Add current page
          crumbs.push({
            label: "New Service",
            href: '/services/new?serviceAccountId=' + serviceAccountId
          });
        }
      }

      /*
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      CASE
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      */
      if (this.isCaseView()) {
        // Add link to home
        crumbs.push({
          label: "Home",
          href: '/'
        });

        // View/Edit Case
        if (!this.isNewCaseView()) {
          if (this.recordData && this.recordData.id) {
            let account = false;
            
            // Add Account link for accounts[0] (if exists)
            if (this.recordData.accounts && this.recordData.accounts.items && this.recordData.accounts.items.length) {
              account = this.recordData.accounts.items[0].account;
              crumbs.push({
                label: account.name,
                href: '/accounts/' + account.id
              });
            }

            if (this.recordData.services && this.recordData.services.items && this.recordData.services.items.length) {
              let service = this.recordData.services.items[0].service;

              // Add account link if not previously added above
              if (!account && service.account && service.account.id) {
                account = service.account;
                crumbs.push({
                  label: account.name,
                  href: '/accounts/' + account.id
                });
              }

              // Add service link
              crumbs.push({
                label: service.name,
                href: '/services/' + service.id
              });
            }

            // Add current page
            crumbs.push({
              label: this.recordData.subject,
              href: '/cases/' + this.$route.params.id
            });
          }
        // New Case
        } else {
          // Add link for Service (if applicable)
          let caseServiceServiceId = this.$route.query.caseServiceServiceId;
          if (caseServiceServiceId) {
            let operation = graphqlOperation(queries.getService, {id: caseServiceServiceId});
            const result = await API.graphql(operation);
            if (result && result.data && result.data.getService) {
              let service = result.data.getService;
              let account = service.account;

              crumbs.push({
                label: account.name,
                href: '/accounts/' + account.id
              });

              crumbs.push({
                label: service.name,
                href: '/services/' + service.id
              });
            }
          }

          // Add link for Account (if applicable)
          let caseAccountAccountId = this.$route.query.caseAccountAccountId;
          if (caseAccountAccountId) {
            let operation = graphqlOperation(queries.getAccount, {id: caseAccountAccountId});
            const result = await API.graphql(operation);
            if (result && result.data && result.data.getAccount) {
              let account = result.data.getAccount;
              crumbs.push({
                label: account.name,
                href: '/accounts/' + account.id
              });
            }
          }

          // Add current page
          crumbs.push({
            label: "New Case",
            href: '/cases/new?caseServiceServiceId=' + caseServiceServiceId
          });
        }
      }

      /*
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      PRODUCT
      ~~~~~~~~~~~~~~~~~~~~~~~~~
      */
      if (this.isProductView()) {
        // View/Edit Product
        if (!this.isNewProductView()) {
          if (this.recordData && this.recordData.id) {
            // Add link to home - ONLY ON EDIT for Product
            crumbs.push({
              label: "Home",
              href: '/'
            });
            let account = this.recordData.account;
            if (account && account.id) {
              crumbs.push({
                label: account.name,
                href: '/accounts/' + account.id
              });
            }

            let services = this.recordData.services;
            if (services.items && services.items.length) {
              let service = services.items[0].service;
              if (service && service.id) {
                crumbs.push({
                  label: service.name,
                  href: '/services/' + service.id
                });
              }
            }

            // Add current page
            crumbs.push({
              label: this.recordData.name,
              href: '/products/' + this.$route.params.id
            });
          }
        }
      }

      this.$Breadcrumbs.items = crumbs;
    },
    isAccountView() {
      return window.document.location.href.indexOf("/#/accounts/") !== -1;
    },
    isNewAccountView() {
      return window.document.location.href.indexOf("/#/accounts/new") !== -1;
    },
    isServiceView() {
      return window.document.location.href.indexOf("/#/services/") !== -1;
    },
    isNewServiceView() {
      return window.document.location.href.indexOf("/#/services/new") !== -1;
    },
    isCaseView() {
      return window.document.location.href.indexOf("/#/cases/") !== -1;
    },
    isNewCaseView() {
      return window.document.location.href.indexOf("/#/cases/new") !== -1;
    },
    isProductView() {
      return window.document.location.href.indexOf("/#/products/") !== -1;
    },
    isNewProductView() {
      return window.document.location.href.indexOf("/#/products/new") !== -1;
    },
    removeServiceProgressBar() {
      let movedServiceProgressBar = window.document.getElementById("movedServiceProgressBar");
      if (movedServiceProgressBar) {
        movedServiceProgressBar.remove();
      }
    },
    resetToolbar() {
      // Reset the toolbar (form controls and tab navigation)
      let topPageToolbar = document.getElementById("topPageToolbar");
      if (topPageToolbar) {
        topPageToolbar.innerHTML = '';
      }
    },
    resetHeader() {
      let objectHeader = window.document.getElementById('objectHeader');
      let pageTitle = window.document.getElementById('pageTitle');
      if (objectHeader && pageTitle) {
        objectHeader.classList.add('md-hide');
        pageTitle.classList.remove('md-hide');
        objectHeader.innerHTML = '';
      }
      this.removeServiceProgressBar();
    },
    updateHeader() {
      let objectHeaderContent = window.document.getElementById('objectHeaderContentHidden');
      let objectHeader = window.document.getElementById('objectHeader');
      let pageTitle = window.document.getElementById('pageTitle');

      if (objectHeaderContent && objectHeaderContent.innerHTML !== '') {
        if (typeof this.list !== 'undefined') {
          if (objectHeaderContent && objectHeaderContent.innerHTML !== '' && objectHeader && pageTitle) {
            objectHeader.innerHTML = objectHeaderContent.innerHTML;
            pageTitle.classList.add('md-hide');
            objectHeader.classList.remove('md-hide');
          }
        }
      }
    },
    resizeHandler() {
      let mainPanelWindow = document.getElementById('mainPanelWindow');
      let topNavbar = document.getElementById("topNavbar");
      let topNavbarHeight = topNavbar.offsetHeight;
      mainPanelWindow.style.height = (document.body.offsetHeight - topNavbarHeight) + 'px';
    },
    relocateTabControls() {
      let mainPanelWindow = window.document.getElementById('mainPanelWindow');
      let topPageToolbar = document.getElementById("topPageToolbar");

      if (mainPanelWindow && topPageToolbar) {                
        /* [i] Add Tab Navigation */
        let tabNavigation = mainPanelWindow.querySelector(".md-tabs.topNavTabs .md-tabs-navigation");
        if (tabNavigation && tabNavigation.parentElement.id !== 'topPageToolbar') {
          topPageToolbar.append(tabNavigation);
        }
      }
    },
  },
  watch: {
    'recordData': {
      immediate: true,
      handler: function(newVal, oldVal) {
          let isCaseView = this.isCaseView();
          this.$nextTick(function () {
            // don't try to relocateTabControls on Case screen
            if (!isCaseView) {
              this.relocateTabControls();
            }
            this.updateBreadcrumbs();
          });
      },
    },
    'formConfig.pageMode': {
      immediate: true,
      handler: function(newVal, oldVal) {
        // If a User clicks on the Edit button when looking at a different tab, switch them back to the form tab.
        if (oldVal == 'view' && newVal == 'edit') {
          let topPageToolbar = window.document.getElementById("topPageToolbar");
          if (topPageToolbar) {
            let navTabs = topPageToolbar.querySelectorAll(".md-tabs-navigation a");
            if (navTabs.length > 1) {
              let firstTab = navTabs[0];
              if (!firstTab.classList.contains("router-link-active")) {
                firstTab.click();
              }
            }
          }
        }
      },
    }
  },
}